import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getDataWithoutParams, getWaitingEngagementConfig } from 'src/consumer/services/featureServices'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'

const COLLAPSE_DELAY_MS = 2000
const SESSION_START_BUFFER_MS = 5 * 60 * 1000

function addUserIdVariants(userSet, uid) {
  if (uid === undefined || uid === null) {
    return
  }

  userSet.add(uid)
  userSet.add(String(uid))

  const numericUid = Number(uid)
  if (!Number.isNaN(numericUid)) {
    userSet.add(numericUid)
  }
}

function removeUserIdVariants(userSet, uid) {
  if (uid === undefined || uid === null) {
    return
  }

  userSet.delete(uid)
  userSet.delete(String(uid))

  const numericUid = Number(uid)
  if (!Number.isNaN(numericUid)) {
    userSet.delete(numericUid)
  }
}

function isLocalUser(uid, localUid) {
  if (uid === undefined || uid === null || localUid === undefined || localUid === null) {
    return false
  }

  return uid === localUid || String(uid) === String(localUid) || Number(uid) === Number(localUid)
}

export function useSessionSupportShell({
  enabled = true,
  inCall = false,
  isPatient = true,
  collapseDrawerForActiveCall = false,
  localUid,
  bookedEventId,
  clinicBrandId,
  supportQrCodeMediaId: externalSupportQrCodeMediaId = null,
  initialEngagementConfig = null,
  sessionStartTime,
  fetchEngagementConfig = false,
}) {
  const [hasMentorJoined, setHasMentorJoined] = useState(false)
  const [isDrawerCollapsed, setIsDrawerCollapsed] = useState(false)
  const [supportQRCodeMediaId, setSupportQRCodeMediaId] = useState(externalSupportQrCodeMediaId)
  const [engagementConfig, setEngagementConfig] = useState(initialEngagementConfig)
  const remoteUsersRef = useRef(new Set())
  const hasAppliedActiveCallCollapseRef = useRef(false)

  useEffect(() => {
    if (externalSupportQrCodeMediaId) {
      setSupportQRCodeMediaId(externalSupportQrCodeMediaId)
    }
  }, [externalSupportQrCodeMediaId])

  useEffect(() => {
    if (initialEngagementConfig) {
      setEngagementConfig(initialEngagementConfig)
    }
  }, [initialEngagementConfig])

  useEffect(() => {
    if (!enabled || !clinicBrandId || externalSupportQrCodeMediaId || supportQRCodeMediaId) {
      return
    }

    let isMounted = true

    const fetchClinicBrandData = async () => {
      try {
        const response = await getDataWithoutParams(
          `/api/external/clinic/brand-and-clinics?id=${clinicBrandId}`,
        )

        if (!isMounted || response?.status !== 200 || !isCommonResponseSuccessful(response?.data?.code)) {
          return
        }

        const qrCodeMediaId =
          response?.data?.data?.clinicBrand?.webAddressForWhatsappInitialMessage?.qrCodeMediaId

        if (qrCodeMediaId) {
          setSupportQRCodeMediaId(qrCodeMediaId)
        }
      } catch (error) {
        console.error('Error fetching clinic brand QR code:', error)
      }
    }

    fetchClinicBrandData()

    return () => {
      isMounted = false
    }
  }, [clinicBrandId, enabled, externalSupportQrCodeMediaId, supportQRCodeMediaId])

  useEffect(() => {
    if (!enabled || !isPatient || !fetchEngagementConfig || !bookedEventId) {
      return
    }

    let isMounted = true

    const fetchConfig = async () => {
      try {
        const result = await getWaitingEngagementConfig(bookedEventId)
        if (isMounted && result?.success && result?.engagement) {
          setEngagementConfig(result.engagement)
        }
      } catch (error) {
        console.error('Error fetching engagement config:', error)
      }
    }

    fetchConfig()

    return () => {
      isMounted = false
    }
  }, [bookedEventId, enabled, fetchEngagementConfig, isPatient])

  const sessionHasStarted = useMemo(() => {
    if (!sessionStartTime) {
      return true
    }

    const now = new Date()
    const startTime = new Date(sessionStartTime)
    return now >= new Date(startTime.getTime() - SESSION_START_BUFFER_MS)
  }, [sessionStartTime])

  const handleRemoteUserJoin = useCallback(
    (user) => {
      if (!enabled || !isPatient) {
        return false
      }

      const userUid = user?.uid
      if (userUid === undefined || userUid === null || isLocalUser(userUid, localUid)) {
        return false
      }

      addUserIdVariants(remoteUsersRef.current, userUid)
      setHasMentorJoined(true)
      return true
    },
    [enabled, isPatient, localUid],
  )

  const handleRemoteUserLeave = useCallback(
    (user) => {
      if (!enabled || !isPatient) {
        return false
      }

      const userUid = user?.uid
      if (userUid === undefined || userUid === null || isLocalUser(userUid, localUid)) {
        return false
      }

      removeUserIdVariants(remoteUsersRef.current, userUid)

      if (remoteUsersRef.current.size === 0) {
        setHasMentorJoined(false)
        setIsDrawerCollapsed(false)
      }

      return true
    },
    [enabled, isPatient, localUid],
  )

  useEffect(() => {
    if (!enabled || !inCall || !hasMentorJoined || !sessionHasStarted) {
      return
    }

    const timer = setTimeout(() => {
      setIsDrawerCollapsed(true)
    }, COLLAPSE_DELAY_MS)

    return () => clearTimeout(timer)
  }, [enabled, hasMentorJoined, inCall, sessionHasStarted])

  useEffect(() => {
    if (!inCall) {
      hasAppliedActiveCallCollapseRef.current = false
      return
    }

    if (
      !enabled ||
      !isPatient ||
      !collapseDrawerForActiveCall ||
      hasAppliedActiveCallCollapseRef.current
    ) {
      return
    }

    hasAppliedActiveCallCollapseRef.current = true
    setIsDrawerCollapsed(true)
  }, [
    collapseDrawerForActiveCall,
    enabled,
    inCall,
    isPatient,
  ])

  return {
    engagementConfig,
    handleRemoteUserJoin,
    handleRemoteUserLeave,
    hasMentorJoined,
    isDrawerCollapsed,
    sessionHasStarted,
    setIsDrawerCollapsed,
    showCollapsedStrip: enabled && isPatient && inCall && isDrawerCollapsed,
    showDrawer: enabled && isPatient && inCall && !isDrawerCollapsed,
    showOnlySupport: hasMentorJoined && sessionHasStarted,
    supportQRCodeMediaId,
  }
}
